import Icon from 'components/Icon'
import { addModal } from 'features/modals/modalSlice'
import { useDeleteItem } from 'hooks'
import React from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { colors, _ } from 'utils'
import ImagePreviewModal from './ImagePreviewModal'

const S = {
    Wrapper: styled.div<{ image: string }>`
        width: 100%;
        height: 100%;
        border-radius: 5px;
        overflow: hidden;
        background: url(${({ image }) => image}) no-repeat center;
        background-size: cover;
        position: relative;
        cursor: pointer;
        &:hover div {
            opacity: 1;
        }
    `,
    Overlay: styled.div<{ isSelected?: boolean }>`
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(0, 0, 0, 0.4);
        opacity: ${({ isSelected }) => (isSelected ? 1 : 0)};
        transition: opacity 0.2s;
    `,
    Icons: styled.div`
        display: flex;
        justify-content: flex-end;
        padding: 8px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;

        & > span {
            &:hover {
                filter: none;
            }
        }
    `,
    Description: styled.div`
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
    `,
    CheckedIcon: styled.div`
        display: flex;
        place-items: center;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        margin: 8px 0 28px 8px;
        border: 3px solid white;
        background-color: ${colors.success};
        & > span {
            margin-bottom: 6px;
        }
    `,
}

interface Props<T> {
    isSelected?: boolean
    image: T
    onSelected: (image?: T) => void
    onDelete: (image?: T) => void
    canDelete: boolean
    getImageUrl: (image: T) => string
}

function ImageItem<T>({
    isSelected,
    image,
    canDelete,
    onSelected,
    onDelete,
    getImageUrl
}: Props<T>) {
    const dispatch = useDispatch()
    const { showDeleteConfirmModal } = useDeleteItem(onDelete)

    const selectable = !!onSelected

    const showPreviewModal = () => {
        dispatch(addModal({
            Component: (
                { close }: { close: () => void }
            ) => (
                <ImagePreviewModal
                    close={close}
                    image={image}
                    canDelete={canDelete}
                    canSelect={selectable}
                    onSelect={() => onSelected(image)}
                    onDelete={() => (
                        showDeleteConfirmModal(image, close)
                    )}
                    getImageUrl={getImageUrl}
                />
            ),
            header: _('gallery.image_preview'),
            padding: 20,
            size: 'xl',
        }))
    }

    return (
        <S.Wrapper
            image={getImageUrl(image)}
            onClick={() => selectable && onSelected(image)}
        >
            {isSelected ? (
                <S.Overlay isSelected={isSelected}>
                    <S.Icons onClick={(e) => e.stopPropagation()}>
                        <S.CheckedIcon>
                            <Icon icon="check" size={18} color="white" />
                        </S.CheckedIcon>
                    </S.Icons>
                    <S.Description>
                        {_('image.selected')}
                    </S.Description>
                </S.Overlay>
            ) : (
                <S.Overlay>
                    <S.Icons onClick={(e) => e.stopPropagation()}>
                        <Icon
                            icon="view"
                            size={18}
                            color="white"
                            className="mr-2"
                            onClick={showPreviewModal}
                        />
                        {canDelete && (
                            <Icon
                                icon="trash"
                                size={18}
                                color="white"
                                onClick={() => showDeleteConfirmModal(image)}
                            />
                        )}
                    </S.Icons>
                    {selectable && (
                        <S.Description>
                            <Icon icon="check" color="white" size={20} className="mb-1 mr-2" />
                            {_('image.click_to_select')}
                        </S.Description>
                    )}
                </S.Overlay>
            )}
        </S.Wrapper>
    )
}

export default ImageItem
